import { createValidity, assertString, phoneNumber } from '@mhub/web-validations';

import INTL_CALL_CODES from '../../constants/intlCallCodes';

export default function required(value, opts) {
  assertString(value);
  const options = Object.assign({}, opts);
  let { key, label, message, validRegex } = options;
  if (key === undefined) {
    key = 'key';
  }
  if (label === undefined) {
    label = 'Field';
  }
  if (message === undefined) {
    message = `${label} is not a valid phone number`;
  }
  if (validRegex === undefined) {
    validRegex = '^\\d+$';
  }
  const e = new RegExp(validRegex, 'i');
  const validity = createValidity(key);
  if (value !== '' && !e.test(value)) {
    validity.isValid = false;
    validity.message = message;
  }
  return validity;
}

export function getPhoneValidRegex(presets, prefix, type) {
  let regex = '^[0-9]*$';
  if (prefix && Array.isArray(presets)) {
    const preset = presets.find(pvp => pvp && pvp.type === type && pvp.prefix === prefix);
    if (preset) {
      regex = preset.regex;
    }
  }
  return regex;
}

// MS-6740: a mobile field holds the national number that follows the selected prefix, and a
// national number never carries the trunk 0 (+62 811605506, not +62 0811605506). Staff often type
// or paste the local form, so the leading 0 is dropped - unless the tenant's own preset regex
// deliberately starts with 0, in which case the value is left exactly as typed.
export function presetExpectsLeadingZero(regex) {
  return /^\^\(?0/.test(regex || '');
}

export function stripMobileTrunkZero(value, presets, prefix) {
  if (typeof value !== 'string' || !prefix || prefix === 'other') return value;
  if (presetExpectsLeadingZero(getPhoneValidRegex(presets, prefix, 'mobile'))) return value;
  return value.replace(/^0+/, '');
}

// Sample national mobile numbers shown in the error so staff see the expected shape.
const MOBILE_EXAMPLES = {
  60: '196756118',
  62: '812345678',
  65: '81234567',
};

export function invalidMobileMessage(t, label, prefix) {
  const callCode = INTL_CALL_CODES.find(c => c.value === prefix);
  if (!t || !callCode) return undefined;
  const example = MOBILE_EXAMPLES[prefix];
  if (example) {
    return t('{{label}} must be a valid {{country}} mobile number, for example {{example}}', {
      label, country: callCode.text, example,
    });
  }
  return t('{{label}} must be a valid {{country}} mobile number', { label, country: callCode.text });
}

// Validation entry for a mobile PhoneInput: the tenant preset for the chosen prefix, checked
// against the value with the trunk 0 removed (the same value the change handler stores), and a
// message naming the country.
export function mobileValidation({ presets, prefix, label, t }) {
  if (prefix === 'other') return { validate: phoneNumber, option: {} };
  return {
    validate: (value, opts) => phoneNumber(stripMobileTrunkZero(value, presets, prefix), opts),
    option: {
      isMobile: true,
      phonePrefix: prefix,
      validRegex: getPhoneValidRegex(presets, prefix, 'mobile'),
      message: invalidMobileMessage(t, label, prefix),
    },
  };
}

// Applies stripMobileTrunkZero to the purchaser's mobile and emergency contact mobile in place.
// Called on every input and prefix change, so a number typed before its prefix is picked is
// still stored without the trunk 0.
export function normalizeMobileFields(customer, presets) {
  if (!customer) return customer;
  [['mobile', 'mobilePrefix'], ['emergencyContactMobile', 'emergencyContactMobilePrefix']]
    .forEach(([field, prefixField]) => {
      if (customer[field]) {
        // the form handlers mutate their state object in place before setState; follow suit
        // eslint-disable-next-line no-param-reassign
        customer[field] = stripMobileTrunkZero(customer[field], presets, customer[prefixField]);
      }
    });
  return customer;
}



// WEBPACK FOOTER //
// ./src/utils/validations/phone.js